import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, MapPin, Calendar, Search, Download, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDonations() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, donation, fruit_bearers

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments/transactions`, { withCredentials: true });
      // Filter only paid transactions
      const paidTransactions = res.data.filter(t => t.payment_status === 'paid');
      setTransactions(paidTransactions);
    } catch (e) { 
      console.error(e);
      toast.error('Failed to load donations');
    }
    finally { setLoading(false); }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = 
      (t.donor_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (t.donor_email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (t.donor_city?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || t.type === filterType;
    
    return matchesSearch && matchesType;
  });

  // Calculate stats
  const totalAmount = filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const donationCount = filteredTransactions.filter(t => t.type === 'donation').length;
  const fruitBearersCount = filteredTransactions.filter(t => t.type === 'fruit_bearers').length;

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'City', 'Amount', 'Type', 'Ministries'];
    const rows = filteredTransactions.map(t => [
      new Date(t.created_at).toLocaleDateString(),
      t.donor_name || 'Anonymous',
      t.donor_email || '',
      t.donor_city || '',
      `$${(t.amount || 0).toFixed(2)}`,
      t.type === 'fruit_bearers' ? 'Fruit Bearers' : 'One-time',
      (t.ministries || []).join(', ')
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV');
  };

  return (
    <div data-testid="admin-donations">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Donations</h1>
          <p className="text-charcoal/60 mt-1">Track and manage all donations</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={fetchTransactions} 
            variant="outline" 
            size="sm"
            className="gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
          <Button 
            onClick={exportToCSV} 
            variant="outline" 
            size="sm"
            className="gap-2"
          >
            <Download size={16} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 border border-gold/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-green-500 p-2 rounded-lg">
              <DollarSign size={18} className="text-white" />
            </div>
            <span className="text-sm text-charcoal/60">Total Raised</span>
          </div>
          <p className="text-2xl font-semibold text-charcoal">${totalAmount.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gold/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-purple p-2 rounded-lg">
              <TrendingUp size={18} className="text-white" />
            </div>
            <span className="text-sm text-charcoal/60">One-time Donations</span>
          </div>
          <p className="text-2xl font-semibold text-charcoal">{donationCount}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gold/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-gold p-2 rounded-lg">
              <DollarSign size={18} className="text-white" />
            </div>
            <span className="text-sm text-charcoal/60">Fruit Bearers</span>
          </div>
          <p className="text-2xl font-semibold text-charcoal">{fruitBearersCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <Input
            placeholder="Search by name, email, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            All
          </Button>
          <Button
            variant={filterType === 'donation' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('donation')}
          >
            One-time
          </Button>
          <Button
            variant={filterType === 'fruit_bearers' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('fruit_bearers')}
          >
            Fruit Bearers
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filteredTransactions.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gold/10 overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-cream">
              <tr>
                <th className="text-left p-4 font-medium text-charcoal">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} />
                    Date
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-charcoal">Donor</th>
                <th className="text-left p-4 font-medium text-charcoal">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} />
                    City
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-charcoal">Amount</th>
                <th className="text-left p-4 font-medium text-charcoal">Type</th>
                <th className="text-left p-4 font-medium text-charcoal">Ministry</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((t, index) => (
                <tr key={t.session_id || index} className="border-t border-cream hover:bg-cream/50">
                  <td className="p-4 text-charcoal/70">
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <p className="text-charcoal font-medium">{t.donor_name || 'Anonymous'}</p>
                    <p className="text-sm text-charcoal/50">{t.donor_email || '-'}</p>
                  </td>
                  <td className="p-4 text-charcoal/70">
                    {t.donor_city || '-'}
                  </td>
                  <td className="p-4">
                    <span className="font-semibold text-green-600">
                      ${(t.amount || 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4">
                    {t.type === 'fruit_bearers' ? (
                      <span className="px-2 py-1 bg-purple/10 text-purple text-xs rounded-full">
                        {t.tier_name || 'Fruit Bearer'}
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        One-time
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-charcoal/60 text-sm">
                    {(t.ministries || ['General']).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
          <DollarSign size={48} className="mx-auto text-charcoal/20 mb-4" />
          <p className="text-charcoal/50">No donations found</p>
          <p className="text-sm text-charcoal/40 mt-2">
            {searchTerm ? 'Try a different search term' : 'Donations will appear here when received'}
          </p>
        </div>
      )}
    </div>
  );
}
